import React, { useEffect, useMemo, useRef, useState } from 'react';

function safeJsonParseFromText(text) {
  if (!text) return null;
  const trimmed = String(text).trim();

  // Strip code fences if present.
  const unfenced = trimmed.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');

  // Try direct parse first.
  try {
    return JSON.parse(unfenced);
  } catch {
    // fallthrough
  }

  // Try to extract the first JSON object.
  const first = unfenced.indexOf('{');
  const last = unfenced.lastIndexOf('}');
  if (first >= 0 && last > first) {
    const slice = unfenced.slice(first, last + 1);
    try {
      return JSON.parse(slice);
    } catch {
      return null;
    }
  }

  return null;
}

function normalizeWhitespace(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getQueryParam(name) {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

function setQueryParam(name, value) {
  const url = new URL(window.location.href);
  if (value) {
    url.searchParams.set(name, value);
  } else {
    url.searchParams.delete(name);
  }
  window.history.replaceState({}, '', url.toString());
}

function buildCssSelector(el) {
  if (!el || el.nodeType !== 1) return '';
  if (el.id) return `#${CSS.escape(el.id)}`;

  const parts = [];
  let node = el;
  while (node && node.nodeType === 1 && node.tagName.toLowerCase() !== 'html') {
    if (node.id) {
      parts.unshift(`#${CSS.escape(node.id)}`);
      break;
    }

    const tag = node.tagName.toLowerCase();
    let index = 1;
    let sib = node;
    while ((sib = sib.previousElementSibling)) {
      if (sib.tagName.toLowerCase() === tag) index += 1;
    }
    parts.unshift(`${tag}:nth-of-type(${index})`);
    node = node.parentElement;
  }
  return parts.join(' > ');
}

function buildCandidateSummariesFromHtml(html, limit = 60) {
  if (!html) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;
  if (!body) return [];

  const candidates = [];
  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode();
  while (node && candidates.length < limit) {
    const el = /** @type {HTMLElement} */ (node);
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'script' || tag === 'style' || tag === 'meta' || tag === 'link') {
      node = walker.nextNode();
      continue;
    }

    const id = el.getAttribute('id') || '';
    const className = el.getAttribute('class') || '';
    const text = normalizeWhitespace(el.textContent || '').slice(0, 140);
    const childCount = el.children ? el.children.length : 0;

    // Very small, empty, or purely structural elements are noise.
    if (!id && !className && !text && childCount === 0) {
      node = walker.nextNode();
      continue;
    }

    const selector = buildCssSelector(el);
    if (!selector) {
      node = walker.nextNode();
      continue;
    }

    candidates.push({
      selector,
      tag,
      id,
      className: className.slice(0, 200),
      childCount,
      text,
    });

    node = walker.nextNode();
  }

  return candidates;
}

function buildDomTreeFromHtml(html, maxNodes = 900) {
  if (!html) return { tree: [], truncated: false };

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;
  if (!body) return { tree: [], truncated: false };

  let count = 0;
  let truncated = false;

  const describeEl = (el) => {
    const tag = (el.tagName || '').toLowerCase();
    const id = el.getAttribute('id') || '';
    const className = el.getAttribute('class') || '';
    const selector = buildCssSelector(el);
    const text = normalizeWhitespace(el.textContent || '').slice(0, 60);
    return { tag, id, className: className.slice(0, 120), selector, text };
  };

  const walk = (rootEl, depth = 0) => {
    if (!rootEl || count >= maxNodes) {
      truncated = true;
      return null;
    }

    count += 1;
    const info = describeEl(rootEl);

    // Skip nodes with no usable selector.
    if (!info.selector) return null;

    const children = [];
    // Keep tree readable: stop at a reasonable depth.
    if (depth < 10) {
      for (const child of Array.from(rootEl.children || [])) {
        if (count >= maxNodes) {
          truncated = true;
          break;
        }
        const childNode = walk(child, depth + 1);
        if (childNode) children.push(childNode);
      }
    }

    return {
      key: info.selector,
      ...info,
      children,
    };
  };

  const roots = [];
  for (const child of Array.from(body.children || [])) {
    if (count >= maxNodes) {
      truncated = true;
      break;
    }
    const node = walk(child, 0);
    if (node) roots.push(node);
  }

  return { tree: roots, truncated };
}

function pickHeuristicRegion(doc, kind) {
  if (!doc || !doc.body) return null;

  const lower = String(kind || '').toLowerCase();
  const byTag = {
    header: ['header'],
    main: ['main'],
    footer: ['footer'],
  };

  const tags = byTag[lower] || [];
  for (const t of tags) {
    const el = doc.querySelector(t);
    if (el) return el;
  }

  const keywordSets = {
    header: ['header', 'masthead', 'topbar', 'navbar', 'nav'],
    main: ['main', 'content', 'page', 'body', 'container'],
    footer: ['footer', 'bottom', 'site-footer'],
  };

  const keywords = keywordSets[lower] || [];
  const els = Array.from(doc.querySelectorAll('section, div, nav, main, header, footer, article'));

  const scored = els
    .map((el) => {
      const id = (el.getAttribute('id') || '').toLowerCase();
      const cls = (el.getAttribute('class') || '').toLowerCase();
      const textLen = normalizeWhitespace(el.textContent || '').length;
      const childCount = el.children ? el.children.length : 0;

      let score = 0;
      for (const k of keywords) {
        if (id.includes(k)) score += 6;
        if (cls.includes(k)) score += 4;
      }
      // Prefer larger containers.
      score += Math.min(10, Math.floor(textLen / 200));
      score += Math.min(6, Math.floor(childCount / 8));
      return { el, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored[0]?.el || null;
}

function extractInlineCss(html) {
  if (!html) return { html, css: '' };
  const styleTagRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let css = '';
  const cleaned = html.replace(styleTagRegex, (_, inner) => {
    css += `\n${inner}`;
    return '';
  });
  return { html: cleaned, css: css.trim() };
}

function stripScripts(html) {
  if (!html) return '';
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Strip iframes/frames to avoid X-Frame-Options issues inside preview.
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/<frame\b[^>]*>/gi, '')
    .replace(/<frameset\b[^<]*(?:(?!<\/frameset>)<[^<]*)*<\/frameset>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '');
}

function stripWizardAttributesFromHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/\sdata-do-outline\s*=\s*"1"/gi, '')
    .replace(/\sdata-do-outline\s*=\s*'1'/gi, '')
    .replace(/\sdata-do-outline\s*=\s*1/gi, '');
}

function decorateHtmlForPreview(html) {
  if (!html) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;
    if (!body) return html;

    const walker = doc.createTreeWalker(body, NodeFilter.SHOW_ELEMENT);
    let node = walker.nextNode();
    while (node) {
      const el = /** @type {HTMLElement} */ (node);
      const tag = (el.tagName || '').toLowerCase();
      if (tag !== 'html' && tag !== 'body' && tag !== 'script' && tag !== 'style') {
        if (el.children && el.children.length > 0) {
          el.setAttribute('data-do-outline', '1');
        }
      }
      node = walker.nextNode();
    }

    return body.innerHTML;
  } catch {
    return html;
  }
}

function buildSrcDoc({ baseUrl, html, templateCss, pageCss }) {
  const baseTag = baseUrl ? `<base href="${baseUrl}">` : '';
  const wizardCss = `
    /* Import Wizard: show structure without touching source HTML */
    [data-do-outline="1"] { outline: 1px dotted #c7c7c7 !important; outline-offset: -1px !important; }
  `;
  const styles = `<style>\n${templateCss || ''}\n</style>\n<style>\n${pageCss || ''}\n</style>\n<style>\n${wizardCss}\n</style>`;

  // Ensure a full document so elementFromPoint works consistently.
  return `<!doctype html><html><head><meta charset="utf-8">${baseTag}${styles}</head><body>${html || ''}</body></html>`;
}

export default function WebpageImportWizard({ pageTitle = 'Import Wizard' }) {
  const iframeRef = useRef(null);
  const splitContainerRef = useRef(null);
  const [step, setStep] = useState(1);

  const [options, setOptions] = useState({ templates: [], pages: [], components: [] });
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [draftId, setDraftId] = useState(getQueryParam('draft') || '');

  const [sourceUrl, setSourceUrl] = useState('');
  const [fetching, setFetching] = useState(false);

  const [rawHtml, setRawHtml] = useState('');
  const [pageHtml, setPageHtml] = useState('');
  const [pageCss, setPageCss] = useState('');

  const [activeTag, setActiveTag] = useState('main');
  const [selections, setSelections] = useState({ header: null, main: null, footer: null, components: [] });

  const [selectedTemplateTid, setSelectedTemplateTid] = useState('');
  const [templateCss, setTemplateCss] = useState('');

  const [hoverBox, setHoverBox] = useState(null);
  const [selectedBox, setSelectedBox] = useState(null);
  const [lastAssigned, setLastAssigned] = useState('');

  const [showHelp, setShowHelp] = useState(false);
  const [showDomTools, setShowDomTools] = useState(true);
  const [domTree, setDomTree] = useState([]);
  const [domTreeTruncated, setDomTreeTruncated] = useState(false);
  const [expandedDomKeys, setExpandedDomKeys] = useState(() => new Set());
  const [domTreeError, setDomTreeError] = useState('');
  const [selectorInput, setSelectorInput] = useState('');

  const [autoDetectBusy, setAutoDetectBusy] = useState(false);
  const [autoDetectNote, setAutoDetectNote] = useState('');

  const [leftPaneWidth, setLeftPaneWidth] = useState(null);
  const [draggingSplit, setDraggingSplit] = useState(false);
  const dragRef = useRef({ startX: 0, startWidth: 0 });

  const previewHtml = useMemo(() => decorateHtmlForPreview(pageHtml), [pageHtml]);

  // Rebuild DOM tree whenever the underlying HTML changes (fetch or draft load).
  useEffect(() => {
    if (!pageHtml) {
      setDomTree([]);
      setDomTreeTruncated(false);
      setDomTreeError('');
      return;
    }

    try {
      const built = buildDomTreeFromHtml(pageHtml, 900);
      setDomTree(built.tree || []);
      setDomTreeTruncated(Boolean(built.truncated));
      setDomTreeError('');

      // Expand the first couple of root nodes for convenience.
      const firstKeys = (built.tree || []).slice(0, 2).map((n) => n.key);
      setExpandedDomKeys((prev) => {
        const next = new Set(prev);
        for (const k of firstKeys) next.add(k);
        return next;
      });
    } catch {
      setDomTree([]);
      setDomTreeTruncated(false);
      setDomTreeError('Unable to build DOM tree for this page.');
    }
  }, [pageHtml]);

  const [aiInstruction, setAiInstruction] = useState('');
  const [aiTarget, setAiTarget] = useState('main');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');

  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageTid, setNewPageTid] = useState('');
  const [newPageDescription, setNewPageDescription] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');

  const srcDoc = useMemo(() => {
    return buildSrcDoc({
      baseUrl: sourceUrl,
      html: previewHtml,
      templateCss,
      pageCss,
    });
  }, [sourceUrl, previewHtml, templateCss, pageCss]);

  const selectionGuidance = useMemo(() => {
    if (!pageHtml) return 'Fetch a page to begin.';
    if (step < 2) return 'Fetch a page to begin.';
    if (!selections.main) return 'Next: assign Main (click the main content container).';
    if (!selections.header) return 'Next: assign Header (click the site header / nav).';
    if (!selections.footer) return 'Next: assign Footer (click the site footer).';
    return 'Next: optionally select Components, run AI, then Save.';
  }, [pageHtml, step, selections.main, selections.header, selections.footer]);

  // Initialize split width when DOM tools become visible.
  useEffect(() => {
    if (!showDomTools) return;
    if (!splitContainerRef.current) return;
    if (leftPaneWidth !== null) return;
    const w = splitContainerRef.current.getBoundingClientRect().width;
    // Default: leave ~380px for tools.
    const next = Math.max(520, Math.min(w - 360, Math.floor(w * 0.72)));
    setLeftPaneWidth(next);
  }, [showDomTools, leftPaneWidth]);

  // Handle dragging splitter.
  useEffect(() => {
    if (!draggingSplit) return;
    const handleMove = (e) => {
      if (!splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const total = rect.width;
      const dx = e.clientX - dragRef.current.startX;
      const nextLeft = dragRef.current.startWidth + dx;
      const minLeft = 420;
      const minRight = 320;
      const maxLeft = total - minRight - 10;
      setLeftPaneWidth(Math.max(minLeft, Math.min(maxLeft, nextLeft)));
    };
    const handleUp = () => setDraggingSplit(false);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [draggingSplit]);

  // Load options.
  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoadingOptions(true);
      try {
        const res = await window.axios.get('/api/v1/webpages/import-wizard/options');
        if (!isMounted) return;
        setOptions(res.data || { templates: [], pages: [], components: [] });
      } catch (e) {
        // keep silent; UI still usable without options
      } finally {
        if (isMounted) setLoadingOptions(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  // Load draft if present.
  useEffect(() => {
    if (!draftId) return;
    let isMounted = true;

    (async () => {
      try {
        const res = await window.axios.get(`/api/v1/webpages/import-wizard/drafts/${draftId}`);
        if (!isMounted) return;
        const s = (res.data && res.data.state) || {};

        setSourceUrl(s.sourceUrl || '');
        setRawHtml(s.rawHtml || '');
        setPageHtml(s.pageHtml || '');
        setPageCss(s.pageCss || '');
        setSelections(s.selections || { header: null, main: null, footer: null, components: [] });
        setSelectedTemplateTid(s.selectedTemplateTid || '');
        setTemplateCss(s.templateCss || '');
        setStep(s.step || 1);

        setNewPageTitle(s.newPageTitle || '');
        setNewPageTid(s.newPageTid || '');
        setNewPageDescription(s.newPageDescription || '');
      } catch (e) {
        // ignore
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [draftId]);

  // Save draft (debounced).
  useEffect(() => {
    const state = {
      step,
      sourceUrl,
      rawHtml,
      pageHtml,
      pageCss,
      selections,
      selectedTemplateTid,
      templateCss,
      newPageTitle,
      newPageTid,
      newPageDescription,
    };

    const handle = window.setTimeout(async () => {
      // Don't save empty drafts.
      if (!sourceUrl && !rawHtml && !pageHtml) return;

      try {
        const res = await window.axios.post('/api/v1/webpages/import-wizard/drafts', {
          draft_id: draftId || null,
          state,
        });
        const id = res.data && res.data.draft_id;
        if (id && id !== draftId) {
          setDraftId(id);
          setQueryParam('draft', id);
        }
      } catch (e) {
        // ignore
      }
    }, 800);

    return () => window.clearTimeout(handle);
  }, [
    step,
    sourceUrl,
    rawHtml,
    pageHtml,
    pageCss,
    selections,
    selectedTemplateTid,
    templateCss,
    newPageTitle,
    newPageTid,
    newPageDescription,
    draftId,
  ]);

  // Load template css when selected.
  useEffect(() => {
    if (!selectedTemplateTid) {
      setTemplateCss('');
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const res = await window.axios.get(`/api/v1/webpages/import-wizard/template/${encodeURIComponent(selectedTemplateTid)}`);
        if (!isMounted) return;
        setTemplateCss((res.data && res.data.css) || '');
      } catch (e) {
        if (!isMounted) return;
        setTemplateCss('');
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [selectedTemplateTid]);

  // Attach iframe hover/click handlers.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let cleanup = null;

    const onLoad = () => {
      try {
        const doc = iframe.contentDocument;
        const win = iframe.contentWindow;
        if (!doc || !win) return;

        const handleMove = (e) => {
          const el = doc.elementFromPoint(e.clientX, e.clientY);
          if (!el || el.tagName.toLowerCase() === 'html' || el.tagName.toLowerCase() === 'body') {
            setHoverBox(null);
            return;
          }
          const rect = el.getBoundingClientRect();
          setHoverBox({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
        };

        const handleLeave = () => setHoverBox(null);

        const handleClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const el = doc.elementFromPoint(e.clientX, e.clientY);
          if (!el || el.tagName.toLowerCase() === 'html' || el.tagName.toLowerCase() === 'body') return;

          const rect = el.getBoundingClientRect();
          setSelectedBox({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });

          const selector = buildCssSelector(el);
          const outerHTML = stripWizardAttributesFromHtml(el.outerHTML);

          setSelections((prev) => {
            if (activeTag === 'component') {
              setLastAssigned(`Added Component: ${selector || '(unknown selector)'}`);
              return {
                ...prev,
                components: [...(prev.components || []), { selector, outerHTML }],
              };
            }

            setLastAssigned(`Set ${activeTag}: ${selector || '(unknown selector)'}`);
            return {
              ...prev,
              [activeTag]: { selector, outerHTML },
            };
          });
        };

        doc.addEventListener('mousemove', handleMove);
        doc.addEventListener('mouseleave', handleLeave);
        doc.addEventListener('click', handleClick, true);

        cleanup = () => {
          doc.removeEventListener('mousemove', handleMove);
          doc.removeEventListener('mouseleave', handleLeave);
          doc.removeEventListener('click', handleClick, true);
        };
      } catch (e) {
        // ignore
      }
    };

    iframe.addEventListener('load', onLoad);

    return () => {
      iframe.removeEventListener('load', onLoad);
      if (cleanup) cleanup();
    };
  }, [activeTag, srcDoc]);

  const fetchPage = async () => {
    setFetching(true);
    setAiError('');
    setAutoDetectNote('');
    setLastAssigned('');
    setSelectedBox(null);
    try {
      const res = await window.axios.post('/api/v1/webpages/import-wizard/fetch', { url: sourceUrl });
      const html = (res.data && res.data.html) || '';

      const safeHtml = stripScripts(html);
      setRawHtml(safeHtml);

      const extracted = extractInlineCss(safeHtml);
      setPageCss(extracted.css);
      setPageHtml(extracted.html);

      setSelections({ header: null, main: null, footer: null, components: [] });
      setStep(2);

      // AI-assisted region detection (best-effort) to preselect main/header/footer.
      // Uses /ai/query (free-form) and falls back to heuristics.
      window.setTimeout(() => {
        autoDetectRegions(extracted.html);
      }, 50);
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.error) || 'Fetch failed';
      setAiError(msg);
    } finally {
      setFetching(false);
    }
  };

  const autoDetectRegions = async (html) => {
    if (!html) return;
    setAutoDetectBusy(true);
    setAutoDetectNote('Detecting header/main/footer…');

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const heuristicHeader = pickHeuristicRegion(doc, 'header');
    const heuristicMain = pickHeuristicRegion(doc, 'main');
    const heuristicFooter = pickHeuristicRegion(doc, 'footer');

    // Always preselect main at least (heuristic). AI may improve.
    if (heuristicMain) {
      const selector = buildCssSelector(heuristicMain);
      setSelections((prev) => ({
        ...prev,
        main: prev.main || { selector, outerHTML: heuristicMain.outerHTML },
      }));
    }

    try {
      const candidates = buildCandidateSummariesFromHtml(html, 60);
      const prompt = [
        'You are helping an editor import an external web page into a site builder.',
        'Task: Identify the best CSS selectors for the page HEADER, MAIN content area, and FOOTER.',
        'Return STRICT JSON only with keys: header_selector, main_selector, footer_selector.',
        'Rules:',
        '- Select the smallest container that still represents the full region.',
        '- Prefer semantic tags (header/main/footer) when present.',
        '- Avoid selecting html, body, or very generic containers unless necessary.',
        '- The selectors must work with querySelector on the provided HTML.',
        '',
        `Source URL: ${sourceUrl || ''}`,
        '',
        'Candidate elements (selector, tag, id, className, childCount, text):',
        JSON.stringify(candidates),
      ].join('\n');

      const res = await window.axios.post('/ai/query', { prompt });
      const raw = (res.data && (res.data.response || res.data)) || '';
      const parsed = safeJsonParseFromText(raw);

      if (parsed && typeof parsed === 'object') {
        const headerSelector = String(parsed.header_selector || '').trim();
        const mainSelector = String(parsed.main_selector || '').trim();
        const footerSelector = String(parsed.footer_selector || '').trim();

        const setIfValid = (target, selector) => {
          if (!selector) return;
          const node = doc.querySelector(selector);
          if (!node) return;
          setSelections((prev) => ({
            ...prev,
            [target]: prev[target] || { selector, outerHTML: node.outerHTML },
          }));
        };

        setIfValid('header', headerSelector);
        setIfValid('main', mainSelector);
        setIfValid('footer', footerSelector);

        setAutoDetectNote('Detected regions (you can adjust by clicking in the preview or using the DOM tools).');
      } else {
        setAutoDetectNote('Auto-detect ran (heuristic). Adjust selections as needed.');
      }
    } catch {
      setAutoDetectNote('Auto-detect ran (heuristic). Adjust selections as needed.');
    } finally {
      setAutoDetectBusy(false);
    }
  };

  const selectByCss = () => {
    setAiError('');
    setDomTreeError('');
    const selector = String(selectorInput || '').trim();
    if (!selector) return;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(pageHtml || '', 'text/html');
      const node = doc.querySelector(selector);
      if (!node) {
        setDomTreeError('Selector did not match any element.');
        return;
      }

      setSelections((prev) => {
        if (activeTag === 'component') {
          setLastAssigned(`Added Component: ${selector}`);
          return {
            ...prev,
            components: [...(prev.components || []), { selector, outerHTML: node.outerHTML }],
          };
        }

        setLastAssigned(`Set ${activeTag}: ${selector}`);
        return {
          ...prev,
          [activeTag]: { selector, outerHTML: node.outerHTML },
        };
      });
    } catch {
      setDomTreeError('Invalid selector.');
    }
  };

  const applyCandidateSelection = (selector) => {
    if (!selector) return;
    setSelectorInput(selector);
    // Reuse selection logic.
    window.setTimeout(selectByCss, 0);
  };

  const toggleExpanded = (key) => {
    setExpandedDomKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderDomNode = (node, depth = 0) => {
    if (!node) return null;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedDomKeys.has(node.key);
    const indent = Math.min(24, depth * 10);
    const labelParts = [`<${node.tag}>`];
    if (node.id) labelParts.push(`#${node.id}`);
    const label = labelParts.join(' ');

    return (
      <div key={node.key} style={{ paddingLeft: indent }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {hasChildren ? (
            <button
              type="button"
              className="btn btn-default btn-xs"
              style={{ padding: '0 6px', lineHeight: '16px' }}
              onClick={() => toggleExpanded(node.key)}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? '-' : '+'}
            </button>
          ) : (
            <span style={{ display: 'inline-block', width: 24 }} />
          )}

          <button
            type="button"
            className="btn btn-link"
            style={{ padding: 0, textAlign: 'left' }}
            title={node.selector}
            onClick={() => applyCandidateSelection(node.selector)}
          >
            <span style={{ fontWeight: 600 }}>{label}</span>
            {node.className ? <span className="text-muted" style={{ marginLeft: 6, fontSize: 12 }}>{node.className}</span> : null}
          </button>
        </div>
        {node.text ? <div className="text-muted" style={{ marginLeft: hasChildren ? 30 : 30, fontSize: 12 }}>{node.text}</div> : null}
        {hasChildren && isExpanded ? node.children.map((c) => renderDomNode(c, depth + 1)) : null}
      </div>
    );
  };

  const applyAiUpdate = async () => {
    setAiBusy(true);
    setAiError('');

    const target = aiTarget;
    const selected = selections[target];
    if (!selected || !selected.outerHTML) {
      setAiBusy(false);
      setAiError('Select a target element first.');
      return;
    }

    try {
      const contextLines = [
        `sourceUrl: ${sourceUrl || ''}`,
        `target: ${target}`,
        selectedTemplateTid ? `template_tid: ${selectedTemplateTid}` : '',
        'Return only raw HTML for the target fragment.',
      ].filter(Boolean);

      const res = await window.axios.post('/ai/web-editor/update', {
        instruction: aiInstruction || 'Improve structure and semantic HTML.',
        html: stripWizardAttributesFromHtml(selected.outerHTML),
        context: contextLines.join('\n'),
      });

      const updatedHtml = (res.data && res.data.html) || '';
      if (!updatedHtml) {
        setAiError('AI returned empty HTML.');
        return;
      }

      const cleanedUpdatedHtml = stripWizardAttributesFromHtml(updatedHtml);

      setSelections((prev) => ({
        ...prev,
        [target]: { ...prev[target], outerHTML: cleanedUpdatedHtml },
      }));

      // Patch the main page html string by selector.
      if (selected.selector) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(pageHtml || '', 'text/html');
        const node = doc.querySelector(selected.selector);
        if (node) {
          node.outerHTML = cleanedUpdatedHtml;
          setPageHtml(doc.body ? doc.body.innerHTML : pageHtml);
        }
      }
    } catch (e) {
      setAiError('AI update failed.');
    } finally {
      setAiBusy(false);
    }
  };

  const createPage = async () => {
    setCreateBusy(true);
    setCreateError('');

    try {
      const res = await window.axios.post('/api/v1/webpages/import-wizard/create-page', {
        title: newPageTitle,
        tid: newPageTid,
        description: newPageDescription,
        html: pageHtml,
        css: pageCss,
        template_tid: selectedTemplateTid || null,
      });

      const id = res.data && res.data.id;
      if (id) {
        window.location.href = `/webpages/react-edit/${id}`;
      }
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.error) || 'Create failed';
      setCreateError(msg);
    } finally {
      setCreateBusy(false);
    }
  };

  return (
    <div>
      <div className="page-header" style={{ marginTop: 0 }}>
        <div className="pull-right">
          <div className="btn-group">
            <button className="btn btn-default btn-sm" type="button" onClick={() => setShowHelp((v) => !v)}>
              {showHelp ? 'Hide Help' : 'Help'}
            </button>
            <button className="btn btn-default btn-sm" type="button" onClick={() => setShowDomTools((v) => !v)} disabled={!pageHtml}>
              {showDomTools ? 'Hide DOM Tools' : 'Show DOM Tools'}
            </button>
          </div>
        </div>
        <h3 style={{ marginTop: 0 }}>{pageTitle}</h3>
      </div>

      {showHelp ? (
        <div className="panel panel-info">
          <div className="panel-heading"><strong>How this wizard works</strong></div>
          <div className="panel-body">
            <ul style={{ marginBottom: 0 }}>
              <li>Step 1: Fetch an external page (scripts are stripped for safety).</li>
              <li>Step 2: Hover to highlight elements; click to assign to Header/Main/Footer/Component.</li>
              <li>Use <strong>DOM Tools</strong> to select by CSS selector or from a DOM tree list.</li>
              <li><strong>Best practice:</strong> pick the smallest container that represents the region (avoid selecting <code>body</code> unless there’s no better choice).</li>
              <li>Auto-detect tries to preselect <strong>Main</strong> for you; always verify the selection.</li>
            </ul>
          </div>
        </div>
      ) : null}

      <div className="alert alert-info" style={{ marginBottom: 10 }}>
        <strong>Next:</strong> {selectionGuidance}
        {lastAssigned ? <span style={{ marginLeft: 8 }} className="text-muted">({lastAssigned})</span> : null}
      </div>

      <div className="btn-group" style={{ marginBottom: 10 }}>
        <button className={`btn btn-default btn-sm ${step === 1 ? 'active' : ''}`} onClick={() => setStep(1)} type="button">1. Load</button>
        <button className={`btn btn-default btn-sm ${step === 2 ? 'active' : ''}`} onClick={() => setStep(2)} type="button" disabled={!pageHtml}>2. Select</button>
        <button className={`btn btn-default btn-sm ${step === 3 ? 'active' : ''}`} onClick={() => setStep(3)} type="button" disabled={!pageHtml}>3. AI</button>
        <button className={`btn btn-default btn-sm ${step === 4 ? 'active' : ''}`} onClick={() => setStep(4)} type="button" disabled={!pageHtml}>4. Preview</button>
        <button className={`btn btn-default btn-sm ${step === 5 ? 'active' : ''}`} onClick={() => setStep(5)} type="button" disabled={!pageHtml}>5. Save</button>
      </div>

      {aiError ? (
        <div className="alert alert-warning" style={{ marginBottom: 10 }}>{aiError}</div>
      ) : null}

      <div className="row" ref={splitContainerRef}>
        <div
          className={pageHtml ? (showDomTools ? '' : 'col-xs-12') : 'col-xs-12'}
          style={pageHtml && showDomTools ? { display: 'flex', width: '100%' } : undefined}
        >
          <div style={pageHtml && showDomTools ? { flex: '0 0 auto', width: leftPaneWidth || '72%', minWidth: 420 } : undefined}>
          {step === 1 ? (
            <div className="panel panel-default">
              <div className="panel-heading"><strong>Load External Page</strong></div>
              <div className="panel-body">
                <div className="form-group">
                  <label>URL</label>
                  <input className="form-control" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://example.com/page" />
                </div>
                <button className="btn btn-primary" onClick={fetchPage} disabled={fetching || !sourceUrl} type="button">
                  {fetching ? 'Fetching…' : 'Fetch'}
                </button>
                {draftId ? <span className="text-muted" style={{ marginLeft: 10 }}>Draft: {draftId}</span> : null}
              </div>
            </div>
          ) : null}

          {step >= 2 ? (
            <div className="panel panel-default">
              <div className="panel-heading">
                <strong>DOM Viewer</strong>
                <span className="text-muted" style={{ marginLeft: 10 }}>Hover highlights. Click assigns to: <strong>{activeTag}</strong></span>
                <span className="text-muted" style={{ marginLeft: 10 }}>
                  {autoDetectBusy ? 'Auto-detecting…' : autoDetectNote ? autoDetectNote : ''}
                </span>
              </div>
              <div className="panel-body" style={{ padding: 0 }}>
                <div style={{ position: 'relative', height: 'calc(100vh - 240px)', minHeight: 720 }}>
                  <iframe
                    ref={iframeRef}
                    title="import-preview"
                    style={{ width: '100%', height: '100%', border: 0 }}
                    sandbox="allow-same-origin"
                    srcDoc={srcDoc}
                  />
                  {hoverBox ? (
                    <div
                      style={{
                        position: 'absolute',
                        top: hoverBox.top,
                        left: hoverBox.left,
                        width: hoverBox.width,
                        height: hoverBox.height,
                        border: '2px solid #337ab7',
                        pointerEvents: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  ) : null}

                  {selectedBox ? (
                    <div
                      style={{
                        position: 'absolute',
                        top: selectedBox.top,
                        left: selectedBox.left,
                        width: selectedBox.width,
                        height: selectedBox.height,
                        border: '2px solid #5cb85c',
                        pointerEvents: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
          </div>

          {pageHtml && showDomTools ? (
            <div
              style={{
                flex: '0 0 10px',
                cursor: 'col-resize',
                background: draggingSplit ? '#d9edf7' : '#f5f5f5',
                borderLeft: '1px solid #ddd',
                borderRight: '1px solid #ddd',
                margin: '0 6px',
              }}
              role="separator"
              aria-orientation="vertical"
              onMouseDown={(e) => {
                if (!splitContainerRef.current) return;
                const currentLeft = leftPaneWidth || splitContainerRef.current.getBoundingClientRect().width * 0.72;
                dragRef.current = { startX: e.clientX, startWidth: currentLeft };
                setDraggingSplit(true);
              }}
              title="Drag to resize"
            />
          ) : null}

          {pageHtml && showDomTools ? (
          <div style={{ flex: '1 1 auto', minWidth: 320, maxWidth: 520 }}>
          <div className="panel panel-default">
            <div className="panel-heading"><strong>Wizard Sidebar</strong></div>
            <div className="panel-body">
              <div className="form-group">
                <label>Assign Clicks As</label>
                <div className="btn-group">
                  <button className={`btn btn-default btn-xs ${activeTag === 'header' ? 'active' : ''}`} onClick={() => setActiveTag('header')} type="button">Header</button>
                  <button className={`btn btn-default btn-xs ${activeTag === 'main' ? 'active' : ''}`} onClick={() => setActiveTag('main')} type="button">Main</button>
                  <button className={`btn btn-default btn-xs ${activeTag === 'footer' ? 'active' : ''}`} onClick={() => setActiveTag('footer')} type="button">Footer</button>
                  <button className={`btn btn-default btn-xs ${activeTag === 'component' ? 'active' : ''}`} onClick={() => setActiveTag('component')} type="button">Component</button>
                </div>
                <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Tip: choose a target, then click in the preview.
                </div>
              </div>

              <div className="form-group">
                <label>Template</label>
                <select className="form-control" value={selectedTemplateTid} onChange={(e) => setSelectedTemplateTid(e.target.value)} disabled={loadingOptions}>
                  <option value="">(none)</option>
                  {(options.templates || []).map((t) => (
                    <option key={t.id} value={t.tid}>{t.title} ({t.tid})</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Selected Regions</label>
                <div className="well well-sm" style={{ marginBottom: 0 }}>
                  <div><strong>Header:</strong> {selections.header?.selector || '(none)'}</div>
                  <div><strong>Main:</strong> {selections.main?.selector || '(none)'}</div>
                  <div><strong>Footer:</strong> {selections.footer?.selector || '(none)'}</div>
                  <div><strong>Components:</strong> {(selections.components || []).length}</div>
                </div>
              </div>

              <div className="form-group">
                <label>DOM Tools</label>
                <div className="well well-sm" style={{ marginBottom: 10 }}>
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="text-muted" style={{ fontWeight: 'normal' }}>Select by CSS selector</label>
                    <div className="input-group">
                      <input
                        className="form-control input-sm"
                        value={selectorInput}
                        onChange={(e) => setSelectorInput(e.target.value)}
                        placeholder="#main, main, .content, header nav, etc."
                      />
                      <span className="input-group-btn">
                        <button className="btn btn-default btn-sm" type="button" onClick={selectByCss}>Select</button>
                      </span>
                    </div>
                  </div>

                  <button
                    className="btn btn-default btn-xs"
                    type="button"
                    onClick={() => autoDetectRegions(pageHtml)}
                    disabled={autoDetectBusy || !pageHtml}
                  >
                    {autoDetectBusy ? 'Detecting…' : 'AI Auto-detect Regions'}
                  </button>
                </div>

                {domTreeError ? <div className="alert alert-warning" style={{ marginBottom: 10 }}>{domTreeError}</div> : null}

                <div className="panel panel-default" style={{ marginBottom: 0 }}>
                  <div className="panel-heading" style={{ padding: '6px 10px' }}>
                    <strong>DOM Tree (click to select)</strong>
                  </div>
                  <div className="panel-body" style={{ maxHeight: 320, overflowY: 'auto', padding: 0 }}>
                    {domTree && domTree.length ? (
                      <div style={{ padding: 8 }}>
                        {domTreeTruncated ? (
                          <div className="text-muted" style={{ fontSize: 12, marginBottom: 6 }}>
                            Tree truncated for performance. Use selector search for deeper nodes.
                          </div>
                        ) : null}
                        {domTree.map((n) => renderDomNode(n, 0))}
                      </div>
                    ) : (
                      <div className="text-muted" style={{ padding: 10 }}>No DOM tree available yet.</div>
                    )}
                  </div>
                </div>
              </div>

              {step === 3 ? (
                <div>
                  <hr />
                  <div className="form-group">
                    <label>AI Target</label>
                    <select className="form-control" value={aiTarget} onChange={(e) => setAiTarget(e.target.value)}>
                      <option value="header">Header</option>
                      <option value="main">Main</option>
                      <option value="footer">Footer</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>AI Instruction</label>
                    <textarea className="form-control" rows={4} value={aiInstruction} onChange={(e) => setAiInstruction(e.target.value)} placeholder="e.g. Convert this to Bootstrap grid, remove inline styles, improve semantics" />
                  </div>
                  <button className="btn btn-primary" onClick={applyAiUpdate} disabled={aiBusy} type="button">
                    {aiBusy ? 'Running…' : 'Run AI Update'}
                  </button>
                </div>
              ) : null}

              {step === 5 ? (
                <div>
                  <hr />
                  <div className="form-group">
                    <label>New Page Title</label>
                    <input className="form-control" value={newPageTitle} onChange={(e) => setNewPageTitle(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>New Page TID</label>
                    <input className="form-control" value={newPageTid} onChange={(e) => setNewPageTid(e.target.value)} placeholder="my-page" />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea className="form-control" rows={3} value={newPageDescription} onChange={(e) => setNewPageDescription(e.target.value)} />
                  </div>
                  {createError ? <div className="alert alert-danger">{createError}</div> : null}
                  <button className="btn btn-success" onClick={createPage} disabled={createBusy || !newPageTitle || !newPageTid} type="button">
                    {createBusy ? 'Saving…' : 'Create Page'}
                  </button>
                </div>
              ) : null}

              <hr />
              <div>
                <strong>Lists</strong>
                <div className="text-muted" style={{ fontSize: 12 }}>Templates: {(options.templates || []).length} · Pages: {(options.pages || []).length} · Components: {(options.components || []).length}</div>
              </div>
            </div>
          </div>

          <div className="panel panel-default">
            <div className="panel-heading"><strong>Quick Actions</strong></div>
            <div className="panel-body">
              <button className="btn btn-default btn-sm" type="button" onClick={() => setStep(3)} disabled={!pageHtml}>Go to AI</button>
              <button className="btn btn-default btn-sm" type="button" onClick={() => setStep(5)} disabled={!pageHtml} style={{ marginLeft: 8 }}>Go to Save</button>
            </div>
          </div>
        </div>
        ) : null}
        </div>
      </div>

      <div className="text-muted" style={{ fontSize: 12 }}>
        Iframe runs with <code>sandbox="allow-same-origin"</code> (no scripts).
      </div>
    </div>
  );
}
